import React, { useCallback, useState } from 'react';

import type { Leaves } from '@aline/common';
import {
  getISO8601DateString,
  NUMBER_FIELD_WARNING,
  shouldShowNumberFieldWarning,
} from '@aline/common';
import { gql } from '@apollo/client';
import { FormControlLabel, RadioGroup } from '@mui/material';
import { isValid, parseISO } from 'date-fns';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { AlineInput } from 'src/AlineInput';
import { AlineDatePicker, AlineRadio, FormikAlineTextarea, SmallAlineButton } from 'src/components';
import type { AiAnswerTextProps } from 'src/editor';
import { QuestionOptionsAutocomplete } from 'src/editor';
import { Apollo } from 'src/gql/generated';
import { useCreateExtractionJobForDocument } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  checkbox: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  editButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

type EditAnswerForm =
  | {
      _typename: 'TextAnswer';
      answer: string;
    }
  | {
      _typename: 'SelectAnswer';
      selectedOptions: Pick<Apollo.QuestionOption, 'id' | '__typename' | 'label'>[];
    };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EditAnswerFieldProps = FieldProps<any, EditAnswerForm>;
interface EditAiAnswerFormProps {
  documentId: string;
  extractionJob: AiAnswerTextProps['extractionJob'];
  onEditClose: () => void;
  question: AiAnswerTextProps['question'];
}

export const answerKey: Leaves<EditAnswerForm> = 'answer';

export function EditAiAnswerForm({
  documentId,
  extractionJob,
  onEditClose,
  question,
}: EditAiAnswerFormProps) {
  const { classes } = useStyles();
  const { updateJobAnswer } = useUpdateJobAnswer();
  const { createExtractionJob } = useCreateExtractionJobForDocument(documentId);
  const { updateJobSelectedOptions } = useUpdateJobSelectedOptions();
  const isOptionsQuestion =
    question.type === Apollo.ExtractionQuestionType.SingleSelect ||
    question.type === Apollo.ExtractionQuestionType.MultiSelect;

  if (extractionJob?.lockedBy) return null;

  return (
    <Formik<EditAnswerForm>
      initialValues={
        isOptionsQuestion
          ? {
              _typename: 'SelectAnswer',
              selectedOptions: extractionJob?.selectedOptions ?? [],
            }
          : { _typename: 'TextAnswer', answer: extractionJob?.answer ?? '' }
      }
      onSubmit={async values => {
        switch (values._typename) {
          case 'TextAnswer': {
            if (!extractionJob) {
              await createExtractionJob({
                variables: {
                  documentId,
                  input: { props: { answer: values.answer }, questionId: question.id },
                },
              });
              onEditClose();
              return;
            }

            await updateJobAnswer(extractionJob.id, values.answer);
            onEditClose();
            return;
          }
          case 'SelectAnswer': {
            if (!extractionJob) {
              await createExtractionJob({
                variables: {
                  documentId,
                  input: {
                    props: {},
                    questionId: question.id,
                    selectedOptionIds: values.selectedOptions.map(o => o.id),
                  },
                },
              });
              onEditClose();
              return;
            }

            await updateJobSelectedOptions(extractionJob.id, values.selectedOptions);
            onEditClose();
            return;
          }
        }
      }}
    >
      {({ dirty, handleReset, handleSubmit, isSubmitting }) => {
        return (
          <Form
            className={classes.form}
            onClick={e => e.stopPropagation()}
            onReset={handleReset}
            onSubmit={handleSubmit}
          >
            <EditAiAnswerInput question={question} />
            <div className={classes.editButtons}>
              <SmallAlineButton
                color="primary"
                variant="outlined"
                onClick={e => {
                  e.stopPropagation();
                  onEditClose();
                  handleReset();
                }}
              >
                Cancel
              </SmallAlineButton>
              <SmallAlineButton
                color="primary"
                disabled={!dirty}
                loading={isSubmitting}
                type="submit"
                variant="contained"
              >
                Save
              </SmallAlineButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

const useInputStyles = makeStyles()(theme => ({
  input: {
    backgroundColor: theme.palette.common.white,
  },
}));

export function EditAiAnswerInput({
  question,
}: {
  question: AiAnswerTextProps['question'];
}): JSX.Element {
  const { classes } = useInputStyles();

  switch (question.type) {
    case Apollo.ExtractionQuestionType.Boolean:
      return <QuestionBooleanType />;
    case Apollo.ExtractionQuestionType.Date:
      return <QuestionDateType />;
    case Apollo.ExtractionQuestionType.Text:
      return (
        <FormikAlineTextarea
          fieldName={answerKey}
          maxRows={10}
          minRows={3}
          placeholder="Type text..."
          showHelperText={false}
        />
      );
    case Apollo.ExtractionQuestionType.Number:
      return (
        <Field name={answerKey}>
          {({ field, form: { setFieldValue } }: FieldProps) => (
            <AlineInput
              className={classes.input}
              helperText={NUMBER_FIELD_WARNING}
              hideHelperText={!shouldShowNumberFieldWarning(field.value)}
              placeholder="Type number..."
              value={field.value}
              onChange={e => {
                setFieldValue(answerKey, e.target.value);
              }}
            />
          )}
        </Field>
      );
    case Apollo.ExtractionQuestionType.SingleSelect:
    case Apollo.ExtractionQuestionType.MultiSelect:
      return (
        <Field>
          {({ form: { setValues, values } }: EditAnswerFieldProps) => {
            return (
              <QuestionOptionsAutocomplete
                className={classes.input}
                inputType="aline"
                multiple={question.type === Apollo.ExtractionQuestionType.MultiSelect}
                questionId={question.id}
                value={
                  values._typename === 'SelectAnswer'
                    ? values.selectedOptions.map(o => o.label)
                    : []
                }
                onChange={selectedOptions => {
                  setValues({
                    _typename: 'SelectAnswer',
                    selectedOptions,
                  } satisfies EditAnswerForm);
                }}
              />
            );
          }}
        </Field>
      );
  }
}

function QuestionDateType(): JSX.Element {
  const { classes } = useInputStyles();
  const [open, setOpen] = useState<boolean>(false);

  return (
    <Field name={answerKey}>
      {({ field, form: { setValues } }: FieldProps) => {
        const parsedFieldValue = typeof field.value === 'string' ? parseISO(field.value) : null;
        // Empty answers parse as Invalid Date; pass null so AlineDatePicker does not remount-loop on NaN time values.
        const parsedValue = parsedFieldValue && isValid(parsedFieldValue) ? parsedFieldValue : null;
        return (
          <AlineDatePicker
            enableFlip={true}
            inputClassName={classes.input}
            open={open}
            setOpen={setOpen}
            value={parsedValue}
            onDateChange={newDate => {
              setValues({
                _typename: 'TextAnswer',
                answer: newDate ? getISO8601DateString(newDate) : '',
              } satisfies EditAnswerForm);
            }}
          />
        );
      }}
    </Field>
  );
}

const useBooleanStyles = makeStyles()(theme => ({
  group: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
  },
  radio: {
    marginLeft: 0,
    marginRight: 0,
  },
}));

function QuestionBooleanType(): JSX.Element {
  const { classes } = useBooleanStyles();

  return (
    <Field name={answerKey}>
      {({ form: { isSubmitting, setValues, values } }: EditAnswerFieldProps) => {
        return (
          <RadioGroup
            className={classes.group}
            value={values._typename === 'TextAnswer' ? values.answer : ''}
            onChange={e => {
              setValues({
                _typename: 'TextAnswer',
                answer: e.target.value,
              } satisfies EditAnswerForm);
            }}
          >
            <FormControlLabel
              className={classes.radio}
              control={<AlineRadio disabled={isSubmitting} />}
              disabled={isSubmitting}
              label="Yes"
              value="true"
            />
            <FormControlLabel
              className={classes.radio}
              control={<AlineRadio disabled={isSubmitting} />}
              disabled={isSubmitting}
              label="No"
              value="false"
            />
          </RadioGroup>
        );
      }}
    </Field>
  );
}

/**
 * The server marks the job as edited and records the original answer on every edit, so these
 * selections include `review` and the previous answer for the report cell's edited notice.
 */
void gql`
  mutation UpdateExtractionJobAnswer($input: UpdateExtractionJobInput!) {
    updateExtractionJob(input: $input) {
      __typename
      id
      answer
      previousAnswer
      review {
        __typename
        reviewAction
        reviewedAt
        reviewedBy {
          ...UserBase
        }
      }
    }
  }
`;

export function useUpdateJobAnswer() {
  const [updateExtractionJobAnswer] = Apollo.useUpdateExtractionJobAnswerMutation();
  const updateJobAnswer = useCallback(
    async (extractionJobId: string, answer: string) => {
      await updateExtractionJobAnswer({
        variables: { input: { extractionJobId, props: { answer } } },
      });
    },
    [updateExtractionJobAnswer]
  );

  return { updateJobAnswer };
}

void gql`
  mutation UpdateExtractionJobSelectedOptions($input: UpdateExtractionJobInput!) {
    updateExtractionJob(input: $input) {
      __typename
      id
      previousSelectedOptions {
        ...QuestionOptionSimple
      }
      review {
        __typename
        reviewAction
        reviewedAt
        reviewedBy {
          ...UserBase
        }
      }
      selectedOptions {
        ...QuestionOptionSimple
      }
    }
  }
`;

export function useUpdateJobSelectedOptions() {
  const [updateExtractionJobSelectedOptions] =
    Apollo.useUpdateExtractionJobSelectedOptionsMutation();
  const updateJobSelectedOptions = useCallback(
    async (
      extractionJobId: string,
      selectedOptions: Pick<Apollo.QuestionOption, 'id' | '__typename' | 'label'>[]
    ) => {
      await updateExtractionJobSelectedOptions({
        variables: {
          input: { extractionJobId, props: {}, selectedOptionIds: selectedOptions.map(o => o.id) },
        },
      });
    },
    [updateExtractionJobSelectedOptions]
  );

  return { updateJobSelectedOptions };
}
